"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AgendaSection = void 0;
const ui_1 = require("@app/component/ui");
const form_grid_layout_1 = require("@app/component/ui/forms/form-grid-layout");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const maybe_1 = require("@app/util/functional/maybe");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const agenda_item_1 = require("../components/agenda-item");
const shift_modal_section_1 = require("../components/shift-modal-section");
const shift_context_1 = require("../shift-context");
const detect_start_duration_overlap_1 = require("../util/detect-start-duration-overlap");
const _getLastPointsEnd = (agendaPoints) => {
    let latest = 0;
    agendaPoints.forEach((ag) => {
        const _ag = (0, maybe_unwrap_1.unwrapMaybe)(ag);
        if ((0, util_1.isUndefined)(_ag))
            return;
        if (!(0, util_1.isValue)(_ag.start))
            return;
        if (!(0, util_1.isValue)(_ag.duration))
            return;
        if (_ag.start + _ag.duration > latest) {
            latest = _ag.start + _ag.duration;
        }
    });
    return latest;
};
const getDefaultAgenda = (agenda) => ({
    done: false,
    duration: 60,
    start: agenda?.length ? _getLastPointsEnd(agenda) : 0,
});
const generateAgendaErrors = (overlaps, period, agenda, index) => {
    const _overlaps = overlaps
        // map so that second index (and second overlap) is always agenda
        .map((o) => 'isField' in o.firstOverlap && !('isField' in o.secondOverlap)
        ? {
            firstOverlap: o.secondOverlap,
            firstOverlapIndex: o.secondOverlapIndex,
            secondOverlap: o.firstOverlap,
            secondOverlapIndex: o.firstOverlapIndex,
        }
        : o);
    if (_overlaps.some((overlap) => overlap.secondOverlapIndex === index && 'isPause' in overlap.firstOverlap)) {
        return {
            hasError: true,
            errorMessage: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('EDIT_SHIFT_AGENDA_OVERLAPS_PAUSE')),
        };
    }
    if (_overlaps.some((overlap) => overlap.secondOverlapIndex === index && 'isField' in overlap.firstOverlap)) {
        return {
            hasError: true,
            errorMessage: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('EDIT_SHIFT_AGENDA_OVERLAPS_TASK')),
        };
    }
    if ((agenda.start ?? 0) < 0) {
        return {
            hasError: true,
            errorMessage: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('ERR_MESSAGE.shift.agendaTaskOutsideShift')),
        };
    }
    const minuteDiff = (0, moment_1.default)(period.end)
        .startOf('minute')
        .diff((0, moment_1.default)(period.start)
        .startOf('minute')
        .add((agenda.start ?? 0) + (agenda.duration ?? 0), 'minutes'), 'minutes');
    if (minuteDiff < 0) {
        return {
            hasError: true,
            errorMessage: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('ERR_MESSAGE.shift.agendaTaskOutsideShift')),
        };
    }
    if (!agenda.customAttributes?.length) {
        // this error only does something if the exception 'error_on_agenda_without_attribute' is enabled for the current organization (some orgs require a custom attribute to be picked on each agenda item).
        // otherwise, it doesn't have any effect.
        const errorObj = util_1.miscUtil.getFilteredContentForOrganization('error_on_agenda_without_attribute', null);
        if (errorObj) {
            return errorObj;
        }
    }
};
const AgendaSection = () => {
    const { formControl, dummyShift, disabledEditation } = (0, shift_context_1.useShift)();
    const period = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'period' });
    const [pausesFixed, pauses] = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: ['pausesFixed', 'pauses'],
    });
    const automaticPauses = new maybe_1.Maybe(dummyShift?.pauses)
        .map((_pauses) => _pauses.filter(util_1.isValue))
        .get(() => []);
    const targetPauses = pausesFixed === false ? automaticPauses : (pauses?.filter(util_1.isValue) ?? []);
    const { fields, remove, update, append } = (0, react_hook_form_1.useFieldArray)({
        control: formControl,
        name: 'agenda',
        keyName: 'agendaFieldId',
        rules: {
            validate: (agendas, form) => {
                const overlaps = (0, detect_start_duration_overlap_1.detectOverlaps)([
                    ...(agendas?.map((f) => ({ ...f, isField: true })) ?? []),
                    ...targetPauses.map((p) => ({ ...p, isPause: true })),
                ]);
                const errors = agendas
                    ?.map((agenda, index) => generateAgendaErrors(overlaps, form.period, agenda, index))
                    .filter(util_1.isDefined);
                return errors?.at(0)?.errorMessage;
            },
        },
    });
    const agendaPauseOverlaps = (0, detect_start_duration_overlap_1.detectOverlaps)([
        ...fields.map((f) => ({ ...f, isField: true })),
        ...targetPauses.map((p) => ({ ...p, isPause: true })),
    ]);
    return (react_1.default.createElement(shift_modal_section_1.ShiftModalSection, { id: 'agenda', headerContent: react_1.default.createElement(ui_1.Button, { style: 'BORDERLESS', ico: 'plus', onClick: () => append(getDefaultAgenda(fields)), disabled: (0, util_1.isDefined)(disabledEditation) }, (0, util_1.t)('ADD_AGENDA')) },
        pausesFixed === false ? (react_1.default.createElement(react_1.default.Fragment, null, automaticPauses.map((_automaticPause, index) => (react_1.default.createElement(ui_1.Text, { key: `agenda-automatic-pause-notice-${index}-${_automaticPause.start ?? ''}-${_automaticPause.duration ?? ''}` }, (0, util_1.t)('EDIT_SHIFT_AGENDA_PAUSE', {
            x: (0, moment_1.default)(period.start)
                .add(_automaticPause.start ?? 0, 'minutes')
                .format('HH:mm'),
            y: _automaticPause.duration ?? 0,
        })))))) : null,
        react_1.default.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '0.5rem' } }, fields.map((field, index) => (react_1.default.createElement(react_1.default.Fragment, { key: field.agendaFieldId },
            react_1.default.createElement("div", { style: {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'end',
                    position: 'relative',
                } },
                react_1.default.createElement("div", { style: { position: 'absolute', bottom: '-1.5rem' } },
                    react_1.default.createElement(ui_1.Button, { color: 'RED', style: 'BORDERLESS', ico: 'delete', onClick: () => {
                            remove(index);
                        }, disabled: (0, util_1.isDefined)(disabledEditation) }, (0, util_1.t)('DELETE')))),
            react_1.default.createElement(form_grid_layout_1.FormGridLayout, null,
                react_1.default.createElement(agenda_item_1.AgendaItem, { index: index, field: field, error: generateAgendaErrors(agendaPauseOverlaps, period, field, index), update: update, overlaps: agendaPauseOverlaps })),
            index !== fields.length - 1 && react_1.default.createElement(ui_1.Separator, null)))))));
};
exports.AgendaSection = AgendaSection;
